import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import { PrimeIcons } from 'primereact/api'
import { CheckboxChangeEvent } from 'primereact/checkbox'
import { InputNumber } from 'primereact/inputnumber'
import { Message } from 'primereact/message'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../components/Panel'
import { InputNumberToggleable } from '../../../../components/inputs'
import { usePanelContext } from '../../../../contexts/panel'
import { useToastContext } from '../../../../contexts/toast'
import { getHandleableError } from '../../../../utils/errors/isXiaError'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../utils/helper/validateFormikFields'
import { ConfigurableComponent } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config/context'
import { SummaryDto } from '../../data/model'

type Props = {
  data: SummaryDto
  onSubmit: (data: SummaryDto) => Promise<void>
  isLoading: boolean
}

interface FormValues extends SummaryDto {}
type FormFieldName = keyof SummaryDto

export const Summary: React.FC<Props> = ({ data, onSubmit, isLoading }) => {
  const { t, i18n } = useTranslation()
  const panelId = 'invoice-summary'
  const { isEdit } = usePanelContext()
  const [isEnableErrorMsg, setIsEnableErrorMsg] = useState<boolean>(false)

  const handleSubmit =
    onSubmit ||
    (async () => {
      return
    })

  const { showToast } = useToastContext()
  const { uiConfig: config } = useConfigContext()

  const { language } = i18n
  const currency = data.currency || 'EUR'

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: { ...data },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationTotalNetItemAmount',
          config: config.invoice.summary.confirmationTotalNetItemAmount,
        },
        {
          fieldName: 'confirmationSurcharge',
          config: config.invoice.summary.confirmationSurcharge,
        },
        {
          fieldName: 'confirmationNetAmount',
          config: config.invoice.summary.confirmationNetAmount,
        },
        {
          fieldName: 'confirmationTaxRate',
          config: config.invoice.summary.confirmationTaxRate,
        },
        {
          fieldName: 'confirmationTaxValue',
          config: config.invoice.summary.confirmationTaxValue,
        },
        {
          fieldName: 'confirmationInvoiceAmount',
          config: config.invoice.summary.confirmationInvoiceAmount,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: data => {
      try {
        // send data in DespatchAdviceData
        handleSubmit(data)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t('common.toasts.editItemSummary'),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          summary: errorMessage,
        })
      }
    },
  })

  const showError = (name: FormFieldName) => {
    const hasConfirmationSurchargeError =
      formik.errors.confirmationSurcharge !== undefined

    return hasConfirmationSurchargeError
      ? !!formik.errors[name]
      : !!(formik.touched[name] && formik.errors[name])
  }

  const isFormFieldValid = (name: FormFieldName) => showError(name)
  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  useEffect(() => {
    if (
      formik.errors.confirmationSurcharge !== undefined ||
      formik.errors.confirmationTotalNetItemAmount !== undefined ||
      formik.errors.confirmationNetAmount !== undefined ||
      formik.errors.confirmationInvoiceAmount !== undefined ||
      formik.errors.confirmationTaxValue !== undefined
    ) {
      setIsEnableErrorMsg(true)
    } else {
      setIsEnableErrorMsg(false)
    }
  }, [
    isEnableErrorMsg,
    formik.errors.confirmationSurcharge,
    formik.errors.confirmationTotalNetItemAmount,
    formik.errors.confirmationNetAmount,
    formik.errors.confirmationInvoiceAmount,
    formik.errors.confirmationTaxValue,
  ])

  const movedComponentIfErrorMsgEnable = (msgIdText: any) => {
    return isEnableErrorMsg ? (
      <div style={{ minHeight: '1.2em' }}>{getFormErrorMessage(msgIdText)}</div>
    ) : (
      <div>{getFormErrorMessage(msgIdText)}</div>
    )
  }

  // ************************************************************
  // components
  // ************************************************************
  // confirmationTotalNetItemAmount
  const confirmationTotalNetItemAmountLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('confirmationTotalNetItemAmount'),
      })}
      htmlFor="confirmationTotalNetItemAmount"
    ></label>
  )

  const confirmationTotalNetItemAmountComponent = (
    <InputNumberToggleable
      id="confirmationTotalNetItemAmount"
      checked={formik.values.confirmationTotalNetItemAmountChecked}
      onCheckedChange={(e: CheckboxChangeEvent) =>
        formik.setFieldValue('confirmationTotalNetItemAmountChecked', e.checked)
      }
      value={formik.values.confirmationTotalNetItemAmount}
      mode="currency"
      currency={currency}
      locale={language}
      onChange={e =>
        formik.setFieldValue('confirmationTotalNetItemAmount', e.value)
      }
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('confirmationTotalNetItemAmount'),
      })}
      min={0}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationTotalNetItemAmountErrorMsg = movedComponentIfErrorMsgEnable(
    'confirmationTotalNetItemAmount',
  )

  const confirmationTotalNetItemAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.summary}
      component={confirmationTotalNetItemAmountComponent}
      label={confirmationTotalNetItemAmountLabel}
      errorMsg={confirmationTotalNetItemAmountErrorMsg}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationSurcharge
  const confirmationSurchargeLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('confirmationSurcharge'),
      })}
      htmlFor="confirmationSurcharge"
    ></label>
  )

  const confirmationSurchargeComponent = (
    <InputNumber
      id="confirmationSurcharge"
      value={formik.values.confirmationSurcharge}
      mode="currency"
      currency={currency}
      locale={language}
      onChange={e => formik.setFieldValue('confirmationSurcharge', e.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('confirmationSurcharge'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationSurchargeErrorMsg = movedComponentIfErrorMsgEnable(
    'confirmationSurcharge',
  )

  const confirmationSurchargeWrapper = (
    <ConfigurableComponent
      config={config.invoice.summary}
      component={confirmationSurchargeComponent}
      label={confirmationSurchargeLabel}
      errorMsg={confirmationSurchargeErrorMsg}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationNetAmount
  const confirmationNetAmountLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('confirmationNetAmount'),
      })}
      htmlFor="confirmationNetAmount"
    ></label>
  )

  const confirmationNetAmountComponent = (
    <InputNumberToggleable
      id="confirmationNetAmount"
      checked={formik.values.confirmationNetAmountChecked}
      onCheckedChange={(e: CheckboxChangeEvent) =>
        formik.setFieldValue('confirmationNetAmountChecked', e.checked)
      }
      value={formik.values.confirmationNetAmount}
      mode="currency"
      currency={currency}
      locale={language}
      onChange={e => formik.setFieldValue('confirmationNetAmount', e.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('confirmationNetAmount'),
      })}
      min={0}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationNetAmountErrorMsg = movedComponentIfErrorMsgEnable(
    'confirmationNetAmount',
  )

  const confirmationNetAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.summary}
      component={confirmationNetAmountComponent}
      label={confirmationNetAmountLabel}
      errorMsg={confirmationNetAmountErrorMsg}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationTaxRate
  const confirmationTaxRateLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('confirmationTaxRate'),
      })}
      htmlFor="confirmationTaxRate"
    ></label>
  )

  const confirmationTaxRateComponent = (
    <InputNumber
      id="confirmationTaxRate"
      value={formik.values.confirmationTaxRate}
      suffix=" %"
      locale={language}
      onChange={e => formik.setFieldValue('confirmationTaxRate', e.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('confirmationTaxRate'),
      })}
      min={0}
      max={100}
      minFractionDigits={2}
      maxFractionDigits={2}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationTaxRateErrorMsg = movedComponentIfErrorMsgEnable(
    'confirmationTaxRate',
  )

  const confirmationTaxRateWrapper = (
    <ConfigurableComponent
      config={config.invoice.summary}
      component={confirmationTaxRateComponent}
      label={confirmationTaxRateLabel}
      errorMsg={confirmationTaxRateErrorMsg}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationTaxValue
  const confirmationTaxValueLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('confirmationTaxValue'),
      })}
      htmlFor="confirmationTaxValue"
    ></label>
  )

  const confirmationTaxValueComponent = (
    <InputNumberToggleable
      id="confirmationTaxValue"
      checked={formik.values.confirmationTaxValueChecked}
      onCheckedChange={(e: CheckboxChangeEvent) =>
        formik.setFieldValue('confirmationTaxValueChecked', e.checked)
      }
      value={formik.values.confirmationTaxValue}
      mode="currency"
      currency={currency}
      locale={language}
      onChange={e => formik.setFieldValue('confirmationTaxValue', e.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('confirmationTaxValue'),
      })}
      min={0}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationTaxValueErrorMsg = movedComponentIfErrorMsgEnable(
    'confirmationTaxValue',
  )

  const confirmationTaxValueWrapper = (
    <ConfigurableComponent
      config={config.invoice.summary}
      component={confirmationTaxValueComponent}
      label={confirmationTaxValueLabel}
      errorMsg={confirmationTaxValueErrorMsg}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationInvoiceAmount
  const confirmationInvoiceAmountLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('confirmationInvoiceAmount'),
      })}
      htmlFor="confirmationInvoiceAmount"
    ></label>
  )

  const confirmationInvoiceAmountComponent = (
    <InputNumberToggleable
      id="confirmationInvoiceAmount"
      checked={formik.values.confirmationInvoiceAmountChecked}
      onCheckedChange={(e: CheckboxChangeEvent) =>
        formik.setFieldValue('confirmationInvoiceAmountChecked', e.checked)
      }
      value={formik.values.confirmationInvoiceAmount}
      mode="currency"
      currency={currency}
      locale={language}
      onChange={e => formik.setFieldValue('confirmationInvoiceAmount', e.value)}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('confirmationInvoiceAmount'),
      })}
      min={0}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationInvoiceAmountErrorMsg = movedComponentIfErrorMsgEnable(
    'confirmationInvoiceAmount',
  )

  const confirmationInvoiceAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.summary}
      component={confirmationInvoiceAmountComponent}
      label={confirmationInvoiceAmountLabel}
      errorMsg={confirmationInvoiceAmountErrorMsg}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      formik={formik}
      header={getConfigTranslation(config.invoice.summary.title)}
      id={panelId}
      visible={config.invoice.summary.enabled}
    >
      <>
        <div className="grid">
          <div className="col">
            <Message
              icon={PrimeIcons.INFO_CIRCLE}
              severity="info"
              text={t('invoice.summary.messages.calculationOnSave')}
            />
          </div>
        </div>

        <div className="formgrid grid">
          {confirmationTotalNetItemAmountWrapper}

          {confirmationSurchargeWrapper}

          {confirmationNetAmountWrapper}
        </div>

        <div className="formgrid grid">
          {confirmationTaxRateWrapper}

          {confirmationTaxValueWrapper}

          {confirmationInvoiceAmountWrapper}
        </div>
      </>
    </PanelContainer>
  )
}
